import {AxiosRequestConfig} from 'axios';
import {spotheroGet} from 'api';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {
    ReservationsSegmented,
    ReservationsDailySegmented,
    ReservationsSegmentedResponse,
} from './reservations.types';

/**
 * Formats the response from the get reservation daily segmented endpoint so that it can be consumed by the UI
 * @param data - The response from the get reservation endpoint
 * @return The formatted response
 * @group DTO Formatters
 */
export const formatGetReservationsDailySegmentedResponse = (
    data: ReservationsSegmentedResponse
): ReservationsSegmented => {
    const formattedData: ReservationsSegmented = camelizeKeys(data);

    const transformedSegments: ReservationsDailySegmented[] =
        formattedData.segments.map((segment) => ({
            ...segment,
            transient: segment.transient,
            monthly: segment.monthly,
            event: segment.event,
            employee: segment.employee ? segment.employee : 0,
            comp: segment.comp ? segment.comp : 0,
            id: segment.timePeriod,
        }));

    return {
        ...formattedData,
        segments: transformedSegments,
    };
};

/**
 * Gets an operator's Reservations Per Half Hour
 *
 * @param facilityId - The ID of the facility for which to get segmented reservations
 * @param date - The date for which to retrieve reservation segments
 * @param compPassesEnabled - Whether comp pass is enabled
 * @param config - Additional Axios configuration options for the request
 * @returns A promise that resolves to the formatted daily segmented reservations data
 */
const getReservationsDailySegmented = async (
    facilityId: number,
    date: string,
    compPassesEnabled: boolean,
    config: AxiosRequestConfig = {}
) => {
    try {
        const compConfiguredParam = convertKeysToSnakeCase({
            compConfigured: compPassesEnabled,
        });
        const response = await spotheroGet<ReservationsSegmentedResponse>(
            `operator-reservations/facilities/${facilityId}/daily/segmented/`,
            {...config, ...(date && {date}), ...compConfiguredParam}
        );

        const formattedResponse = formatGetReservationsDailySegmentedResponse(
            response.data.data
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw new Error(error.message);
    }
};

export default getReservationsDailySegmented;
